import { useEffect, useState } from "react";
import { supabase } from "../auth";
import {
  completeTask,
  createTask,
  listTasksForPlant,
} from "../db/tasks";
import type { TaskRow, TaskCategory } from "@garden/types/db";
import "./PlantTasksTab.css";

interface Props {
  plantId: string;
}

const CATEGORIES: { value: TaskCategory; label: string }[] = [
  { value: "care", label: "Care" },
  { value: "growing", label: "Growing" },
  { value: "project", label: "Project" },
  { value: "purchase", label: "Purchase" },
  { value: "maintenance", label: "Maintenance" },
];

export function PlantTasksTab({ plantId }: Props) {
  const [rows, setRows] = useState<TaskRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [completing, setCompleting] = useState<string | null>(null);

  const reload = async () => {
    if (!supabase) return;
    try {
      const data = await listTasksForPlant(supabase, plantId);
      setRows(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void reload();
  }, [plantId]);

  const onCreated = () => {
    setShowForm(false);
    void reload();
  };

  const onComplete = async (taskId: string) => {
    if (!supabase) return;
    setCompleting(taskId);
    try {
      await completeTask(supabase, { task_id: taskId });
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setCompleting(null);
    }
  };

  if (rows === null && !error) {
    return <div className="TasksTab__loading">Loading…</div>;
  }

  const open = (rows ?? []).filter((t) => t.status === "pending");
  const done = (rows ?? []).filter((t) => t.status !== "pending");

  return (
    <div className="TasksTab">
      {open.length > 0 && (
        <ul className="TasksTab__list">
          {open.map((t) => (
            <li key={t.id} className="TasksTab__row">
              <button
                type="button"
                className="TasksTab__tick"
                aria-label={`Mark "${t.name}" complete`}
                disabled={completing === t.id}
                onClick={() => void onComplete(t.id)}
              />
              <div className="TasksTab__body">
                <div className="TasksTab__name">{t.name}</div>
                <div className="TasksTab__meta">
                  {t.due_date ? `Due ${formatDate(t.due_date)}` : "No due date"} ·{" "}
                  {labelForCategory(t.category)}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {!showForm && (
        <button
          type="button"
          className="TasksTab__add"
          onClick={() => setShowForm(true)}
        >
          + Add task
        </button>
      )}

      {showForm && (
        <TaskForm
          plantId={plantId}
          onCreated={onCreated}
          onCancel={() => setShowForm(false)}
        />
      )}

      {done.length > 0 && (
        <>
          <div className="TasksTab__divider">Completed</div>
          <ul className="TasksTab__list TasksTab__list--done">
            {done.map((t) => (
              <li key={t.id} className="TasksTab__row TasksTab__row--done">
                <span className="TasksTab__tick TasksTab__tick--done" aria-hidden="true" />
                <div className="TasksTab__body">
                  <div className="TasksTab__name">{t.name}</div>
                  <div className="TasksTab__meta">
                    {t.status === "skipped" ? "Skipped" : "Done"} ·{" "}
                    {labelForCategory(t.category)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {rows && rows.length === 0 && !showForm && (
        <p className="TasksTab__empty">No tasks yet. One date each, no recurrence.</p>
      )}

      {error && <p className="TasksTab__error">{error}</p>}
    </div>
  );
}

function TaskForm({
  plantId,
  onCreated,
  onCancel,
}: {
  plantId: string;
  onCreated: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState<TaskCategory>("care");
  const [dueDate, setDueDate] = useState<string>("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) return;
    if (name.trim() === "") return;
    setSaving(true);
    setError(null);
    try {
      await createTask(supabase, {
        name: name.trim(),
        category,
        plant_id: plantId,
        due_date: dueDate === "" ? null : dueDate,
        description: description.trim() === "" ? null : description,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  };

  return (
    <form className="TaskForm" onSubmit={onSubmit}>
      <label className="TaskForm__field">
        <span>Task name</span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Hard prune to 30cm"
          required
          maxLength={200}
          autoFocus
        />
      </label>
      <div className="TaskForm__row">
        <label className="TaskForm__field">
          <span>Category</span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as TaskCategory)}
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </label>
        <label className="TaskForm__field">
          <span>Due date</span>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </label>
      </div>
      <label className="TaskForm__field">
        <span>Description (optional)</span>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          placeholder="Anything you want to remember about this task."
        />
      </label>
      {error && <p className="TaskForm__error">{error}</p>}
      <div className="TaskForm__actions">
        <button type="button" className="TaskForm__btn" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button
          type="submit"
          className="TaskForm__btn TaskForm__btn--primary"
          disabled={saving || name.trim() === ""}
        >
          {saving ? "Saving…" : "Add task"}
        </button>
      </div>
    </form>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function labelForCategory(c: string): string {
  const found = CATEGORIES.find((x) => x.value === c);
  return found ? found.label : c;
}
